import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Ghost, ScanLine, LogIn, RefreshCw, CheckCircle2, XCircle, Clock, CalendarX, Ban, KeyRound, ShieldAlert, ShieldCheck, ClipboardCheck, AlertTriangle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  valid:      { label: 'VALID',      color: 'bg-emerald-500 text-black border-emerald-300', Icon: CheckCircle2, sound: 880 },
  used:       { label: 'ALREADY USED', color: 'bg-blue-500 text-white border-blue-300',   Icon: Clock,        sound: 440 },
  wrong_date: { label: 'WRONG DATE',  color: 'bg-orange-500 text-black border-orange-300', Icon: CalendarX,    sound: 220 },
  refunded:   { label: 'REFUNDED',    color: 'bg-red-600 text-white border-red-300',       Icon: Ban,          sound: 180 },
  canceled:   { label: 'CANCELED',    color: 'bg-red-600 text-white border-red-300',       Icon: Ban,          sound: 180 },
  invalid:    { label: 'INVALID',     color: 'bg-neutral-700 text-white border-neutral-400', Icon: XCircle,    sound: 120 },
};

let audioCtx = null;
function beep(freq) {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.connect(g); g.connect(audioCtx.destination);
    o.frequency.value = freq;
    o.type = 'sine';
    g.gain.setValueAtTime(0.001, audioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, audioCtx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3);
    o.start(); o.stop(audioCtx.currentTime + 0.32);
  } catch {}
}

export default function ScannerPage() {
  const [authed, setAuthed] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [missingWaiverAlert, setMissingWaiverAlert] = useState(null);
  const [wrongDateAlert, setWrongDateAlert] = useState(null);
  const [overrideBusy, setOverrideBusy] = useState(false);
  const scannerRef = useRef(null);
  const scanningRef = useRef(false);
  const lastTokenRef = useRef({ token: null, at: 0 });

  // Auto-auth if admin token present
  useEffect(() => {
    const t = localStorage.getItem('access_token');
    if (t) {
      axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
        .then(r => {
          if (r.data?.role === 'admin') setAuthed(true);
        })
        .catch(() => {});
    }
  }, []);

  const doScan = useCallback(async (token, { override = false } = {}) => {
    // Debounce same-token within 3s — but NEVER debounce overrides (admin explicitly clicked)
    if (!override) {
      const now = Date.now();
      if (lastTokenRef.current.token === token && now - lastTokenRef.current.at < 3000) return;
      lastTokenRef.current = { token, at: now };
    }

    try {
      const body = { token };
      if (override) body.override = true;
      const adminTok = localStorage.getItem('access_token');
      const headers = adminTok ? { Authorization: `Bearer ${adminTok}` } : {};
      if (!adminTok && pin) body.door_pin = pin;
      const r = await axios.post(`${API}/api/hh/scan`, body, { headers });
      const status = String(r.data?.status || 'invalid').toLowerCase();
      beep((STATUS_META[status] || STATUS_META.invalid).sound);
      setResult({ ...r.data, _status: status });
      if (navigator.vibrate) navigator.vibrate(status === 'valid' ? [80] : [40, 60, 40]);
      // Wrong-date popup — admin can override and accept the risk
      if (status === 'wrong_date') {
        setWrongDateAlert({
          token,
          order_num: r.data?.order_num || '',
          buyer_name: r.data?.buyer_name || '',
          expected_date: r.data?.expected_date || '',
          today: r.data?.today || '',
          role: r.data?.role || '',
        });
        if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
      }
      // Trigger the "NO WAIVER" popup on any scan that would normally proceed to entry
      const wv = String(r.data?.waiver_status || '').toLowerCase();
      if ((status === 'valid' || status === 'used') && wv && wv !== 'signed') {
        setMissingWaiverAlert({
          order_num: r.data?.order_num || '',
          buyer_name: r.data?.buyer_name || '',
          waiver_status: wv,
          role: r.data?.role || '',
        });
        if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
      }
    } catch (e) {
      setResult({ _status: 'invalid', reason: e?.response?.data?.detail || 'error' });
      beep(120);
    }
  }, [pin]);

  const confirmWrongDateOverride = useCallback(async () => {
    if (!wrongDateAlert || overrideBusy) return;
    setOverrideBusy(true);
    try {
      await doScan(wrongDateAlert.token, { override: true });
    } finally {
      setOverrideBusy(false);
      setWrongDateAlert(null);
    }
  }, [wrongDateAlert, overrideBusy, doScan]);

  const startScanner = useCallback(async () => {
    setError('');
    try {
      const html5 = new Html5Qrcode('scanner-region', { verbose: false });
      scannerRef.current = html5;
      await html5.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
        (decodedText) => { doScan(decodedText); },
        () => {},
      );
      setScanning(true);
      scanningRef.current = true;
    } catch (e) {
      setError('Camera permission denied or unavailable. Please allow camera access.');
    }
  }, [doScan]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current && scanningRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      try { await scannerRef.current.clear(); } catch {}
      scanningRef.current = false;
      setScanning(false);
    }
  }, []);

  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const tryPin = async () => {
    setPinError('');
    // ping /api/hh/scan with a bogus token but the PIN; server will 401 if wrong
    try {
      const r = await axios.post(`${API}/api/hh/scan`, { token: '__PING__', door_pin: pin });
      if (r.data?.status === 'invalid') {
        setAuthed(true);
      }
    } catch (e) {
      if (e?.response?.status === 401) setPinError('Wrong PIN');
      else setPinError('Unable to verify PIN — try again');
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-neutral-900 border border-neutral-800 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Ghost className="text-orange-500" size={22} />
            <h1 className="font-heading text-xl tracking-widest uppercase">Door Scanner</h1>
          </div>
          <p className="text-xs text-neutral-400">Enter the door PIN, or log in as admin.</p>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Door PIN</label>
            <input type="password" inputMode="numeric" value={pin} onChange={e => setPin(e.target.value)}
              data-testid="scanner-pin-input" placeholder="••••"
              className="w-full bg-black border border-neutral-700 focus:border-orange-500 rounded-lg px-3 py-3 text-center font-mono text-lg tracking-widest outline-none" />
            {pinError && <p className="text-xs text-red-400 mt-1">{pinError}</p>}
          </div>
          <button onClick={tryPin} data-testid="scanner-pin-submit"
            className="w-full bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-sm py-3 rounded-full inline-flex items-center justify-center gap-2">
            <KeyRound size={14} /> Unlock
          </button>
          <a href="/admin/login" className="block text-center text-xs text-neutral-400 hover:text-orange-400">
            <LogIn className="inline mr-1" size={12} /> Log in as admin
          </a>
        </div>
      </div>
    );
  }

  const meta = result ? (STATUS_META[result._status] || STATUS_META.invalid) : null;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-md mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ghost className="text-orange-500" size={18} />
            <h1 className="font-heading text-sm tracking-widest uppercase">Door Scanner</h1>
          </div>
          <button onClick={() => setResult(null)} className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1">
            <RefreshCw size={12} /> Reset
          </button>
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-950">
          <div id="scanner-region" className="w-full aspect-square" />
          {!scanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button onClick={startScanner} data-testid="scanner-start"
                className="bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-sm px-6 py-3 rounded-full inline-flex items-center gap-2">
                <ScanLine size={16} /> Start Camera
              </button>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <XCircle className="text-red-500 mb-2" size={32} />
              <p className="text-sm">{error}</p>
              <button onClick={startScanner} className="mt-3 text-xs text-orange-400 underline">Try again</button>
            </div>
          )}
        </div>

        {result && meta && (
          <div data-testid="scanner-result" className={`rounded-2xl border-2 p-5 ${meta.color}`}>
            <div className="flex items-center gap-3">
              <meta.Icon size={40} />
              <div className="flex-1">
                <p className="font-heading text-2xl tracking-widest uppercase leading-none">{meta.label}</p>
                {result.order_num && <p className="text-xs mt-1 font-mono">{result.order_num}</p>}
                {result.buyer_name && <p className="text-xs mt-0.5">{result.buyer_name}</p>}
              </div>
              {result.role && (
                <div data-testid="scan-role-badge"
                  className={`font-heading text-sm tracking-widest uppercase px-3 py-2 rounded-lg border-2 ${
                    result.role === 'kid_0_5'
                      ? 'bg-neutral-800 text-white border-neutral-400'
                      : result.role === 'kid_6_9'
                        ? 'bg-emerald-600 text-white border-emerald-300'
                        : 'bg-orange-600 text-white border-orange-300'
                  }`}>
                  {result.role === 'kid_0_5' ? 'KID 0–5 · FREE' : result.role === 'kid_6_9' ? 'KID 6–9' : 'ADULT'}
                </div>
              )}
            </div>
            <div className="mt-3 text-xs space-y-0.5">
              {result.admission_number && <div>Admission #{result.admission_number}{result.total_in_order ? ` of ${result.total_in_order}` : ''}</div>}
              {result.event_date && <div>Event Date: {result.event_date}</div>}
              {result.ticket_type && <div>Type: {result.ticket_type}</div>}
              {result.used_at && <div>Previously used: {new Date(result.used_at).toLocaleString()}</div>}
              {result.expected_date && <div>Ticket for: {result.expected_date} (today is {result.today})</div>}
            </div>

            {/* Admin-override admission badge — shown when a wrong-date ticket was manually accepted */}
            {result.override_admitted && (
              <div
                data-testid="scan-override-badge"
                className="mt-3 inline-flex items-center gap-2 bg-black/40 border-2 border-yellow-300 text-yellow-100 font-heading text-xs tracking-widest uppercase px-3 py-1.5 rounded-lg"
              >
                <AlertTriangle size={14} /> Admitted via Override · Ticket was for {result.override_expected_date}
              </div>
            )}

            {/* Waiver status — shown on every valid/used result */}
            <WaiverStatusRow result={result} />
          </div>
        )}

        <p className="text-[10px] text-neutral-500 text-center">All times in Asia/Tokyo. Use the same phone for the whole shift so scans stay in sequence.</p>
      </div>

      {/* Wrong-date popup — admin can override and accept the risk */}
      {wrongDateAlert && (
        <div
          className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4"
          data-testid="scanner-wrong-date-alert"
          onClick={() => !overrideBusy && setWrongDateAlert(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="w-full max-w-sm bg-neutral-950 border-2 border-orange-500 rounded-2xl p-6 text-center space-y-3"
            style={{ boxShadow: '0 0 40px rgba(249,115,22,0.55)' }}
          >
            <CalendarX className="text-orange-400 mx-auto animate-pulse" size={56} />
            <h3 className="font-heading text-2xl tracking-widest uppercase text-orange-300">Wrong Date</h3>
            <p className="text-sm text-neutral-200">
              This ticket is for <b className="text-orange-200">{wrongDateAlert.expected_date}</b> —
              today is <b className="text-orange-200">{wrongDateAlert.today}</b>.
            </p>
            <div className="rounded-lg border border-neutral-800 bg-black/60 p-3 text-xs font-mono space-y-0.5 text-left">
              {wrongDateAlert.order_num && <div>Order: <span className="text-orange-300">{wrongDateAlert.order_num}</span></div>}
              {wrongDateAlert.buyer_name && <div>Buyer: {wrongDateAlert.buyer_name}</div>}
              {wrongDateAlert.role && <div>Role: {wrongDateAlert.role.replace('_', ' ').toUpperCase()}</div>}
            </div>
            <p className="text-[11px] text-yellow-200/90 inline-flex items-center justify-center gap-1">
              <AlertTriangle size={12} /> You can admit anyway and accept the risk.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setWrongDateAlert(null)}
                disabled={overrideBusy}
                data-testid="scanner-wrong-date-cancel"
                className="bg-neutral-800 hover:bg-neutral-700 text-white font-heading tracking-widest uppercase text-xs py-3 rounded-full disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmWrongDateOverride}
                disabled={overrideBusy}
                data-testid="scanner-wrong-date-override"
                className="bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-xs py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {overrideBusy ? <Loader2 className="animate-spin" size={12} /> : <AlertTriangle size={12} />}
                {overrideBusy ? 'Admitting…' : 'Admit Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Missing-waiver popup — non-blocking; staff can acknowledge (paper waiver signed) */}
      {missingWaiverAlert && (
        <div
          className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4"
          data-testid="scanner-waiver-alert"
          onClick={() => setMissingWaiverAlert(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="w-full max-w-sm bg-neutral-950 border-2 border-red-500 rounded-2xl p-6 text-center space-y-3"
            style={{ boxShadow: '0 0 40px rgba(220,38,38,0.6)' }}
          >
            <ShieldAlert className="text-red-400 mx-auto animate-pulse" size={56} />
            <h3 className="font-heading text-2xl tracking-widest uppercase text-red-300">No Waiver Detected</h3>
            <p className="text-sm text-neutral-200">
              This ticket has {missingWaiverAlert.waiver_status === 'pending_guardian' ? (
                <>a waiver <b className="text-yellow-300">still awaiting guardian approval</b>.</>
              ) : (
                <>no signed digital waiver on file.</>
              )}
            </p>
            <div className="rounded-lg border border-neutral-800 bg-black/60 p-3 text-xs font-mono space-y-0.5 text-left">
              {missingWaiverAlert.order_num && <div>Order: <span className="text-orange-300">{missingWaiverAlert.order_num}</span></div>}
              {missingWaiverAlert.buyer_name && <div>Buyer: {missingWaiverAlert.buyer_name}</div>}
              {missingWaiverAlert.role && <div>Role: {missingWaiverAlert.role.replace('_', ' ').toUpperCase()}</div>}
            </div>
            <p className="text-[11px] text-neutral-400">
              Ask the guest to sign a physical waiver before entry, or open their phone to complete the digital one.
            </p>
            <button
              onClick={() => setMissingWaiverAlert(null)}
              data-testid="scanner-waiver-alert-ack"
              className="w-full bg-red-600 hover:bg-red-500 text-white font-heading tracking-widest uppercase text-sm py-3 rounded-full inline-flex items-center justify-center gap-2"
            >
              <ClipboardCheck size={14} />
              Paper Waiver Signed — Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Inline row on each scan result card that shows waiver status
function WaiverStatusRow({ result }) {
  const ws = String(result?.waiver_status || '').toLowerCase();
  if (!ws) return null;
  if (ws === 'signed') {
    return (
      <div
        data-testid="scan-waiver-status"
        className="mt-3 inline-flex items-center gap-2 bg-black/40 border-2 border-emerald-300 text-emerald-200 font-heading text-xs tracking-widest uppercase px-3 py-1.5 rounded-lg"
      >
        <ShieldCheck size={14} /> Waiver Signed
      </div>
    );
  }
  if (ws === 'pending_guardian') {
    return (
      <div
        data-testid="scan-waiver-status"
        className="mt-3 inline-flex items-center gap-2 bg-black/40 border-2 border-yellow-300 text-yellow-100 font-heading text-xs tracking-widest uppercase px-3 py-1.5 rounded-lg"
      >
        <ShieldAlert size={14} /> Waiver Pending Guardian
      </div>
    );
  }
  return (
    <div
      data-testid="scan-waiver-status"
      className="mt-3 inline-flex items-center gap-2 bg-black/60 border-2 border-red-400 text-red-100 font-heading text-xs tracking-widest uppercase px-3 py-1.5 rounded-lg animate-pulse"
    >
      <ShieldAlert size={14} /> No Waiver Detected
    </div>
  );
}
